import React, { useCallback, useEffect, useRef, useState } from 'react'
import { List, Input, Button, Empty, Spin } from 'antd'
import type { FormInstance } from 'antd/es/form'
import { searchSiesaItems } from '../../services/OrderAssemblyService'
import { applySiesaItemToForm } from './applySiesaItemToForm'

const SEARCH_RESULTS_LIMIT = 250

export interface SearchSiesaRowProps {
  form: FormInstance
  referenceInputRef?: React.MutableRefObject<any>
  /** Bodega elegida para todo el pedido; manda sobre la que trae el ítem. */
  defaultWarehouse?: string | null
  contractResults?: boolean // when true, hide results (used when reference already set)
  disabled?: boolean // when true, disable search UI until order type selected
  selectedOrderType?: string | null
}

const SearchSiesaRow: React.FC<SearchSiesaRowProps> = ({ form, referenceInputRef, defaultWarehouse, disabled, selectedOrderType }) => {
  const [q, setQ] = useState('')
  const [refQ, setRefQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<any[]>([])
  const [selectedItem, setSelectedItem] = useState<any | null>(null)
  const searchCacheRef = useRef<Map<string, any[]>>(new Map())
  const mounted = useRef(true)

  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])

  const hasReference = Boolean(selectedItem || form.getFieldValue('reference'))
  const inputsDisabled = Boolean(disabled || hasReference)
  const hasAnyQuery = Boolean(String(q || '').trim() || String(refQ || '').trim())

  // Hide results when we already have a reference selected in the form.
  // Avoid depending on contractResults here because it can become stale upstream.
  const visibleResults = hasReference ? [] : results

  const doSearch = useCallback(async (term: string, opts: { by?: 'description' | 'reference' | 'term' } = { by: 'term' }) => {
    const normalizedTerm = String(term || '').trim()
    if (!normalizedTerm || normalizedTerm.length < 2) {
      setResults([])
      return
    }
    const mode = opts.by || 'term'
    const cacheKey = `${mode}:${normalizedTerm.toLowerCase()}`
    const cached = searchCacheRef.current.get(cacheKey)
    if (cached) {
      setResults(cached)
      return
    }
    setLoading(true)
    try {
      let items: any[] = []
      if (opts.by === 'reference') {
        items = await searchSiesaItems({ reference: normalizedTerm, limit: SEARCH_RESULTS_LIMIT })
      } else if (opts.by === 'description') {
        items = await searchSiesaItems({ description: normalizedTerm, limit: SEARCH_RESULTS_LIMIT })
      } else {
        items = await searchSiesaItems({ term: normalizedTerm, limit: SEARCH_RESULTS_LIMIT })
      }
      if (!mounted.current) return
      const list = Array.isArray(items) ? items : []
      const ql = normalizedTerm.toLowerCase()
      // client-side filter: match reference, description or name (if present)
      const filtered = list.filter((it: any) => {
        const ref = String(it.reference || '').toLowerCase()
        const desc = String(it.description || '').toLowerCase()
        const name = String((it as any).name || '').toLowerCase()
        return ref.includes(ql) || desc.includes(ql) || name.includes(ql)
      })
      // deduplicate by reference
      const seen = new Set<string>()
      const unique: any[] = []
      for (const it of filtered) {
        const key = String(it.reference || JSON.stringify(it))
        if (!seen.has(key)) {
          seen.add(key)
          unique.push(it)
        }
      }
      searchCacheRef.current.set(cacheKey, unique)
      setResults(unique)
    } catch (e) {
      console.error('searchSiesaItems error', e)
      setResults([])
    } finally {
      if (mounted.current) setLoading(false)
    }
  }, [])

  // Use explicit search trigger (button or Enter) to avoid many requests while typing

  const handleSelect = useCallback(async (item: any) => {
    try {
      await applySiesaItemToForm(item, form, referenceInputRef, { defaultWarehouse })
      // after selection, collapse results by clearing them
      setResults([])
      setSelectedItem(item)
    } catch (e) {
      console.error('applySiesaItemToForm failed', e)
    }
  }, [form, referenceInputRef, defaultWarehouse])

  const clearSelection = useCallback(() => {
    setSelectedItem(null)
    // allow user to search again
    setResults([])
    setQ('')
    setRefQ('')
    try { 
      form.setFieldsValue({ 
        reference: undefined,
        item_description: undefined,
        item_short_description: undefined,
        item_short_reference: undefined,
        extOptions: [],
        selectedExtensions: "[]",
        availablePrices: []
      }) 
    } catch {}
  }, [form])

  return (
    <div style={{ marginBottom: 12 }}>
      {!hasReference && (
        <div
          style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 8,
        maxWidth: 700,
          }}
        >
          <Input.Search
        placeholder={inputsDisabled ? (hasReference ? "Referencia seleccionada " : "Seleccione tipo CPT/CPV/CPE antes de buscar") : "Buscar por descripción..."}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onSearch={(value) => { if (!inputsDisabled) { setQ(value); doSearch(value, { by: 'description' }) } }}
        enterButton="Buscar"
        allowClear
        size="middle"
        disabled={inputsDisabled}
          />
          <Input.Search
        placeholder={inputsDisabled ? (hasReference ? "Referencia seleccionada " : "Seleccione tipo CPT/CPV/CPE antes de buscar") : "Buscar por referencia..."}
        value={refQ}
        onChange={(e) => setRefQ(e.target.value)}
        onSearch={(value) => { if (!inputsDisabled) { setRefQ(value); doSearch(value, { by: 'reference' }) } }}
        enterButton="Buscar Ref"
        allowClear
        size="middle"
        disabled={inputsDisabled}
          />
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}><Spin /></div>
        ) : selectedItem ? (
          <div 
            className={`order-assembly-theme-${selectedOrderType || 'CPT'}`}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12, 
              padding: '8px 12px', 
              background: 'var(--oa-accent)', 
              border: '1px solid rgba(var(--oa-accent-rgb), 0.2)', 
              borderRadius: 4, 
              color: '#fff' 
            }}
          >
            <div style={{ fontWeight: 600 }}>{selectedItem.reference}</div>
            <div style={{  flex: 1 }}>{selectedItem.description}</div>
            <Button 
              type="link" 
              onClick={clearSelection} 
              style={{
                color: "#fff",
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '4px',
                padding: '0 8px',
                height: '24px',
                lineHeight: '24px',
                fontSize: '12px',
                border: '1px solid rgba(255,255,255,0.4)'
              }}
            >
              Cambiar
            </Button>
          </div>
        ) : visibleResults.length === 0 ? (
          hasAnyQuery ? <Empty description="Sin resultados" /> : null
        ) : (
          <>
            <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>
              Mostrando {visibleResults.length} referencias
            </div>
            <List
              size="small"
              bordered
              dataSource={visibleResults}
              style={{ maxHeight: 220, overflowY: 'auto' }}
              renderItem={(it: any) => (
                <List.Item
                  key={it.reference || it.item_id || JSON.stringify(it)}
                  onClick={() => handleSelect(it)}
                  style={{ cursor: 'pointer', padding: '12px 16px' }}
                >
                  <List.Item.Meta
                    title={`${it.reference}${it.warehouse_description ? ` - ${it.warehouse_description}` : ''}`}
                    description={it.description}
                  />
                  <div>
                    <Button onClick={(e) => { e.stopPropagation(); handleSelect(it) }} size="small">Seleccionar</Button>
                  </div>
                </List.Item>
              )}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default SearchSiesaRow
